import { useEffect, useState } from 'react';
import type { ConditionOp, DefaultRule, FieldDef, FieldOption, FormSettings, FormType } from '@jfh/intake-shared';
import {
  iqContactFieldOptions,
  iqFieldOptions,
  suggestContactField,
  iqMappingKeyFor,
  iqMappingsOf,
  iqTargetOf,
  resolveRepeaterSubFields,
  resolveStaticOptions,
  setIqTarget,
  suggestIqField,
} from '@jfh/intake-shared';
import { api } from '../../lib/api.js';
import { Icon } from '../../components/Icon.js';
import { IqTargetSelect } from '../IqTargetSelect.js';
import { FORMAT_PRESETS as PRESETS, formatKind, type FormatKind, type RuleCandidate } from './logic.js';
import { MiniButton, SegButtons, ToggleRow } from './ui.js';
import { useIqSpecFields } from '../../lib/useIqSpecFields.js';

const TEXTY = ['short_text', 'long_text', 'email', 'phone', 'number', 'date', 'vat'];
const FORMAT_TYPES = ['short_text', 'long_text', 'email', 'phone', 'number', 'vat', 'sort_code'];
const CHOICE_TYPES = ['select', 'radio', 'checkbox', 'multiselect'];
const DEFAULT_OPS: [ConditionOp, string][] = [
  ['eq', 'is'],
  ['neq', 'is not'],
  ['contains', 'includes'],
  ['in', 'is one of (commas)'],
  ['gt', 'is more than'],
  ['lt', 'is less than'],
  ['not_empty', 'has been answered'],
  ['empty', 'is left blank'],
];

/** The answers a starting-answer picker offers (iQ lists resolved live). */
function useAnswerOptions(f: FieldDef): FieldOption[] {
  const iqKey = f.optionsSource?.type === 'iq' ? f.optionsSource.key : null;
  const [iqOptions, setIqOptions] = useState<FieldOption[]>([]);
  useEffect(() => {
    if (!iqKey) {
      setIqOptions([]);
      return;
    }
    let live = true;
    api
      .getLookup(iqKey)
      .then((r) => live && setIqOptions(r.options))
      .catch(() => live && setIqOptions([]));
    return () => {
      live = false;
    };
  }, [iqKey]);
  return iqKey ? iqOptions : resolveStaticOptions(f);
}

export interface MoreOptionsProps {
  field: FieldDef;
  formType: FormType;
  candidates: RuleCandidate[];
  update: (patch: Partial<FieldDef>) => void;
}

/**
 * More options (F9 P2): who answers it, which team, Accounts card, starting
 * answer (and "change it based on another answer"), format checks, and storing
 * a different value from the one applicants see.
 */
export function MoreOptions({ field: f, formType, candidates, update }: MoreOptionsProps) {
  const isCredit = formType === 'credit_account';
  const isChoice = CHOICE_TYPES.includes(f.type);
  const isBoolCheckbox = f.type === 'checkbox' && !resolveStaticOptions(f).length && !f.optionsSource;
  const pickDefault = f.type === 'select' || f.type === 'radio';
  const showDefault = TEXTY.includes(f.type) || pickDefault || isBoolCheckbox;
  const answers = useAnswerOptions(f);
  const kind = formatKind(f);
  const [testText, setTestText] = useState('');

  const dw = f.defaultWhen;
  const dwDriver = dw ? candidates.find((c) => c.id === dw.fieldId) : undefined;
  const setDw = (patch: Partial<NonNullable<FieldDef['defaultWhen']>>) =>
    update({ defaultWhen: { ...dw!, ...patch } });
  const setDwRule = (i: number, patch: Partial<DefaultRule>) =>
    setDw({ rules: dw!.rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  let testResult: { text: string; ok: boolean } | null = null;
  if (kind === 'regex' && testText) {
    try {
      const ok = new RegExp(f.validation?.pattern ?? '').test(testText);
      testResult = { text: ok ? '✓ Accepted' : '✗ Rejected', ok };
    } catch {
      testResult = { text: 'The pattern has an error', ok: false };
    }
  }

  if (f.type === 'heading') {
    return (
      <div className="field-help" style={{ marginTop: 0 }}>
        Nothing else to set for a step.
      </div>
    );
  }

  return (
    <>
      <div>
        <label className="field-label">Who answers this?</label>
        <SegButtons
          options={[
            ['customer', 'The applicant'],
            ['staff', 'JFH staff, while approving'],
          ]}
          value={f.audience}
          onChange={(audience) => update({ audience })}
        />
        <div className="field-help">Staff questions appear on the Teams card and in the portal, never on the public form.</div>
      </div>

      {f.audience === 'staff' && (isCredit || f.staffStage) && (
        <div>
          <label className="field-label">Which team fills it in?</label>
          <SegButtons
            options={[
              ['cs', 'Customer Service (stage 1)'],
              ['accounts', 'Accounts (stage 2)'],
            ]}
            value={f.staffStage === 'accounts' ? 'accounts' : 'cs'}
            onChange={(staffStage) => update({ staffStage })}
          />
          <div className="field-help">Credit forms have two approvals. Accounts see Customer Service’s answers as read-only.</div>
        </div>
      )}

      {(isCredit || f.cardSummary) && (
        <ToggleRow
          title="Highlight on the Accounts Teams card"
          hint="Shows in “Key details” at the top of the stage 2 card."
          on={Boolean(f.cardSummary)}
          onToggle={() => update({ cardSummary: f.cardSummary ? undefined : true })}
        />
      )}

      {showDefault && (
        <div style={{ display: 'grid', gap: 8 }}>
          {isBoolCheckbox ? (
            <ToggleRow
              title="Ticked to start with"
              hint="They can untick it."
              on={f.defaultValue === 'true'}
              onToggle={() => update({ defaultValue: f.defaultValue === 'true' ? undefined : 'true' })}
            />
          ) : (
            <>
              <label className="field-label" style={{ margin: 0 }}>
                Starting answer
              </label>
              {pickDefault ? (
                <select
                  className="select"
                  value={f.defaultValue ?? ''}
                  onChange={(e) => update({ defaultValue: e.target.value || undefined })}
                >
                  <option value="">None</option>
                  {answers.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                  value={f.defaultValue ?? ''}
                  placeholder="None"
                  onChange={(e) => update({ defaultValue: e.target.value || undefined })}
                />
              )}
              <div className="field-help" style={{ marginTop: 0 }}>
                Filled in already. They can change it.
              </div>
            </>
          )}

          {!dw ? (
            <button
              type="button"
              className="btn ghost sm"
              style={{ justifySelf: 'start', color: 'var(--accent-text)' }}
              disabled={!candidates.length}
              onClick={() =>
                update({
                  defaultWhen: {
                    fieldId: (candidates.find((c) => c.options) ?? candidates[0])?.id ?? '',
                    rules: [{ op: 'eq', equals: '', value: '' }],
                  },
                })
              }
            >
              <Icon name="plus" size={13} /> Change it based on another answer
            </button>
          ) : (
            <div
              style={{
                display: 'grid',
                gap: 6,
                padding: 10,
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                background: 'var(--bg-surface)',
              }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-2)' }}>
                <span>Based on</span>
                <select
                  className="select"
                  style={{ height: 30, fontSize: 12.5, flex: 1, minWidth: 0 }}
                  value={dw.fieldId}
                  onChange={(e) => setDw({ fieldId: e.target.value })}
                >
                  {!dwDriver && <option value={dw.fieldId}>(deleted question)</option>}
                  {candidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              {dw.rules.map((r, i) => {
                const op = r.op ?? 'eq';
                const noValue = op === 'empty' || op === 'not_empty';
                return (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-2)' }}>
                    <span>If it</span>
                    <select
                      className="select"
                      style={{ height: 30, fontSize: 12.5, width: 'auto' }}
                      value={op}
                      onChange={(e) => setDwRule(i, { op: e.target.value as ConditionOp })}
                    >
                      {DEFAULT_OPS.map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                    {!noValue &&
                      (dwDriver?.options && op !== 'in' ? (
                        <select
                          className="select"
                          style={{ height: 30, fontSize: 12.5, width: 'auto', minWidth: 90 }}
                          value={r.equals}
                          onChange={(e) => setDwRule(i, { equals: e.target.value })}
                        >
                          <option value="">…</option>
                          {dwDriver.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="input"
                          style={{ height: 30, fontSize: 12.5, width: 100 }}
                          value={r.equals}
                          onChange={(e) => setDwRule(i, { equals: e.target.value })}
                        />
                      ))}
                    <span>start with</span>
                    {pickDefault ? (
                      <select
                        className="select"
                        style={{ height: 30, fontSize: 12.5, flex: 1, minWidth: 100 }}
                        value={r.value}
                        onChange={(e) => setDwRule(i, { value: e.target.value })}
                      >
                        <option value="">…</option>
                        {answers.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="input"
                        style={{ height: 30, fontSize: 12.5, flex: 1, minWidth: 100 }}
                        value={r.value}
                        onChange={(e) => setDwRule(i, { value: e.target.value })}
                      />
                    )}
                    <MiniButton label="Remove" onClick={() => setDw({ rules: dw.rules.filter((_, j) => j !== i) })}>
                      <Icon name="x" size={12} />
                    </MiniButton>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setDw({ rules: [...dw.rules, { op: 'eq', equals: '', value: '' }] })}
                >
                  <Icon name="plus" size={12} /> Another
                </button>
                <button type="button" className="btn ghost sm" onClick={() => update({ defaultWhen: undefined })}>
                  Remove
                </button>
              </div>
              <div className="field-help" style={{ marginTop: 0 }}>
                The first that matches wins. If none match, the starting answer above is used.
              </div>
            </div>
          )}
        </div>
      )}

      {FORMAT_TYPES.includes(f.type) && (
        <div style={{ display: 'grid', gap: 6 }}>
          <label className="field-label" style={{ margin: 0 }}>
            Check the format
          </label>
          <select
            className="select"
            value={kind}
            onChange={(e) => {
              const k = e.target.value as FormatKind;
              update({
                validation:
                  k === 'none'
                    ? undefined
                    : k === 'maxLength'
                      ? { maxLength: f.validation?.maxLength ?? 255 }
                      : k === 'regex'
                        ? { pattern: f.validation?.pattern ?? '' }
                        : { pattern: PRESETS[k] },
              });
            }}
          >
            <option value="none">Don’t check</option>
            <option value="email">Looks like an email address</option>
            <option value="phone">UK phone number</option>
            <option value="vat">UK VAT number (GB and 9 digits)</option>
            <option value="maxLength">No longer than…</option>
            <option value="regex">Custom pattern (for developers)</option>
          </select>
          {kind === 'maxLength' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)' }}>
              <input
                className="input"
                type="number"
                min={1}
                style={{ width: 90, height: 32 }}
                value={f.validation?.maxLength ?? 255}
                aria-label="Maximum characters"
                onChange={(e) => update({ validation: { maxLength: Number(e.target.value) || undefined } })}
              />
              <span>characters</span>
            </div>
          )}
          {kind === 'regex' && (
            <>
              <input
                className="input mono"
                style={{ height: 32, fontSize: 12.5 }}
                value={f.validation?.pattern ?? ''}
                placeholder="^[A-Z0-9]+$"
                aria-label="Pattern"
                onChange={(e) => update({ validation: { pattern: e.target.value } })}
              />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  className="input"
                  style={{ height: 32, fontSize: 12.5 }}
                  value={testText}
                  placeholder="Type an answer to test it"
                  aria-label="Test answer"
                  onChange={(e) => setTestText(e.target.value)}
                />
                {testResult && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      color: testResult.ok ? 'var(--st-approved)' : 'var(--st-declined)',
                    }}
                  >
                    {testResult.text}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {isChoice && !f.optionsSource && !isBoolCheckbox && (
        <div style={{ display: 'grid', gap: 8 }}>
          <ToggleRow
            title="Store a different value"
            hint="For when iQ needs a code, like “GBP”, and applicants see “Pounds sterling”."
            on={Boolean(f.choices)}
            onToggle={() =>
              update(
                f.choices
                  ? { choices: undefined, options: f.choices.map((c) => c.label || c.value) }
                  : { choices: (f.options ?? []).map((o) => ({ value: o, label: o })), options: undefined },
              )
            }
          />
          {f.choices && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-3)' }}>
                <span>Applicants see</span>
                <span>Stored as</span>
              </div>
              {f.choices.map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <input
                    className="input"
                    style={{ height: 32, fontSize: 12.5 }}
                    value={c.label}
                    aria-label="Shown as"
                    onChange={(e) => update({ choices: f.choices!.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                  />
                  <input
                    className="input mono"
                    style={{ height: 32, fontSize: 12 }}
                    value={c.value}
                    aria-label="Stored as"
                    onChange={(e) => update({ choices: f.choices!.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

export interface ConnectionsProps {
  field: FieldDef;
  formType: FormType;
  settings: FormSettings;
  update: (patch: Partial<FieldDef>) => void;
  onSettingsChange: (patch: Partial<FormSettings>) => void;
}

/**
 * Connections (F9 P2): which iQ field the answer goes to (reads and writes the
 * form's iQ mappings), iQ contact fields per sub-question, and the Wix field.
 */
export function Connections({ field: f, formType, settings, update, onSettingsChange }: ConnectionsProps) {
  const key = iqMappingKeyFor(f, formType);
  const target = iqTargetOf(settings, key, f.id);
  const liveFields = useIqSpecFields();
  const options = iqFieldOptions(formType, settings, liveFields);
  const used = new Set(iqMappingsOf(settings, key).map((m) => m.target));
  const suggestion = target ? '' : suggestIqField(f.label, options);
  const isRepeat = f.type === 'contact' || f.type === 'repeater';
  const wixLinked = settings.source === 'wix' || Boolean(settings.wixFormId);
  const setTarget = (t: string) => onSettingsChange(setIqTarget(settings, key, f.id, t));

  return (
    <>
      <div style={{ display: 'grid', gap: 6 }}>
        <label className="field-label" style={{ margin: 0 }}>
          Which iQ field does this answer go to?
        </label>
        <IqTargetSelect value={target} options={options} used={used} onChange={setTarget} />
        {suggestion && (
          <button
            type="button"
            className="btn sm"
            style={{ justifySelf: 'start', height: 'auto', minHeight: 30, whiteSpace: 'normal', textAlign: 'left', overflowWrap: 'anywhere' }}
            onClick={() => setTarget(suggestion)}
          >
            <Icon name="check" size={13} /> Use the suggestion: <span className="mono">{suggestion}</span>
          </button>
        )}
        <div className="field-help" style={{ marginTop: 0 }}>
          {key === 'iqApprovalMappings'
            ? 'Sent when Accounts approve the credit (stage 2), updating the customer already in iQ.'
            : 'Sent when the application is approved and the record is created in iQ. This changes the form settings, so it goes live straight away.'}
        </div>
      </div>

      {isRepeat && (
        <div style={{ display: 'grid', gap: 6 }}>
          <label className="field-label" style={{ margin: 0 }}>
            iQ contact fields
          </label>
          {resolveRepeaterSubFields(f).map((s, i, subs) => {
            const match = suggestContactField(s);
            return (
              <div key={s.id} style={{ display: 'grid', gap: 3, fontSize: 12.5 }}>
                <span style={{ color: 'var(--text-2)' }}>{s.label}</span>
                <div style={{ display: 'grid', gap: 4 }}>
                  <IqTargetSelect
                    compact
                    value={s.iqField ?? ''}
                    options={iqContactFieldOptions(liveFields)}
                    used={new Set(subs.filter((x) => x.id !== s.id).map((x) => x.iqField || suggestContactField(x) || ''))}
                    emptyLabel={match ? `Matched from the wording: ${match}` : 'Not sent to iQ'}
                    ariaLabel={`iQ field for ${s.label}`}
                    onChange={(t) =>
                      update({
                        subFields: subs.map((x, j) => (j === i ? { ...x, iqField: t.trim() || undefined } : x)),
                      })
                    }
                  />
                </div>
              </div>
            );
          })}
          <div className="field-help" style={{ marginTop: 0 }}>
            Left as it is, a line goes to the iQ field its wording matches (First name → FirstName); one that matches
            nothing isn’t sent.
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 6 }}>
        <label className="field-label" style={{ margin: 0 }}>
          Wix field
        </label>
        {wixLinked || f.wixKey ? (
          <>
            <input
              className="input mono"
              value={f.wixKey ?? ''}
              placeholder="e.g. email_address or contact.email"
              onChange={(e) => update({ wixKey: e.target.value.trim() || undefined })}
            />
            <div className="field-help" style={{ marginTop: 0 }}>
              Which answer on the Wix form fills this question.
            </div>
          </>
        ) : (
          <div className="field-help" style={{ marginTop: 0 }}>
            Only needed when the form is linked to a Wix form (Form settings).
          </div>
        )}
      </div>
    </>
  );
}

